import { Handshake } from "lucide-react";
import { NavigationItem } from "../../types";
import { BuilderContext } from "../context";

/**
 * Sales — the revenue-side CRM entities split out of the old "Customers"
 * group: contacts, companies, deals, offerings, contracts, plus the sales
 * outreach (sequences). Regrouped into nested submenus (Records, Outreach,
 * Contracts) so the dropdown stays short; the desktop renderer opens each group
 * as a flyout and mobile nests it. Group `href` points at the first VISIBLE
 * child so clicking the group never lands on a page the user cannot see.
 * Pipeline is intentionally omitted until a /pipeline route exists.
 */
export function buildSalesSection(ctx: BuilderContext): NavigationItem | null {
  const { isCRM, isAdmin, isEmployee, featureFlags } = ctx;

  // Available to CRM users, employees, and admins
  if (!(isCRM || isAdmin || isEmployee)) return null;

  const topLevel: NavigationItem[] = [];

  // CRM Overview - CRM/Admin only (kept top-level for one-click access)
  if (isCRM || isAdmin) {
    topLevel.push({ id: "crm-overview", name: "CRM Overview", href: "/crm" });
  }

  // --- Records: core CRM entities — Available to all (employees, CRM, admin) ---
  const recordChildren: NavigationItem[] = [];
  if (featureFlags.canViewClients) {
    recordChildren.push(
      { id: "contacts", name: "Contacts", href: "/contacts" },
      { id: "companies", name: "Companies", href: "/companies" },
      { id: "deals", name: "Deals", href: "/deals" },
      { id: "offerings", name: "Offerings", href: "/offerings" },
    );
  }

  // --- Outreach: sequences + communications ---
  const outreachChildren: NavigationItem[] = [];
  // Sales Sequences — email-gated rollout (canViewSequences set from the user's
  // email in enhanced-navigation.tsx; not a role/feature flag).
  if (featureFlags.canViewSequences) {
    outreachChildren.push(
      { id: "sequences-today", name: "Today (Outreach)", href: "/today" },
      { id: "sequences", name: "Sequences", href: "/sequences" },
    );
  }
  // Communications — outbound/log (lives under Sales for now, not Marketing)
  outreachChildren.push({
    id: "crm-activities",
    name: "Communications",
    href: "/crm/communications",
  });

  // --- Contracts: formal, e-signature documents ---
  const contractChildren: NavigationItem[] = [
    { id: "crm-contracts", name: "Contracts", href: "/contracts" },
    { id: "crm-contracts-sent", name: "Sent Contracts", href: "/contracts/sent" },
    { id: "crm-contract-management", name: "Contract Management", href: "/contracts/manage" },
  ];

  const groups: Array<{ id: string; name: string; children: NavigationItem[] }> = [
    { id: "sales-records", name: "Records", children: recordChildren },
    { id: "sales-outreach", name: "Outreach", children: outreachChildren },
    { id: "sales-contracts", name: "Contracts", children: contractChildren },
  ];
  for (const group of groups) {
    if (group.children.length === 0) continue;
    topLevel.push({
      id: group.id,
      name: group.name,
      href: group.children[0].href,
      children: group.children,
    });
  }

  return {
    id: "sales",
    name: "Sales",
    href: "/crm",
    icon: Handshake,
    visible: true,
    order: 1,
    children: topLevel,
  };
}
